// @ts-nocheck
import InputAdornment from '@mui/material/InputAdornment';
import { Box } from '@mui/material';
import { XGSelect } from '@xg-cl/xg-cl';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { XCircle, WarningCircle, User } from '@phosphor-icons/react';
import Editor from '../../../Editor/Editor';
import { setSelectedQuantity } from '../../../../redux/slices/booking/booking';
import {
  getBookingPlanDropdownOptions,
  getPlanTotal,
  planIsPastDeadline,
  planIsSoldOut
} from '../../../../utils/booking';
import { formatCurrency } from '../../../../utils/currencyHelper';

import * as PropTypes from 'prop-types';
import './BookingPlanCard.css';

/**
 * Functional React component for rendering a custom booking plan card.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {Object} [props.data] - The data for the booking plan card.
 * @param {string} [props.id] - The ID for the delete booking plan card component.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the booking plan card component.
 */

const BookingPlanCard = ({ data, id, ...rest }) => {
  const { t } = useTranslation(['bookings'], { useSuspense: false });
  const { title, selectedQuantity, tripplanid, description } = data;
  const dispatch = useDispatch();
  const selectOptions = getBookingPlanDropdownOptions(data);
  const planTotal = getPlanTotal(data);

  const handleChange = (event, option) => {
    const { value } = option.props;
    dispatch(setSelectedQuantity({ tripplanid, quantity: Number(value) }));
  };

  return (
    <Box
      id={id}
      data-testid='booking-plan-card'
      {...rest}
      className={`booking-plan__card ${planIsSoldOut(data) || planIsPastDeadline(data) ? 'plan_unavailable' : ''}`}
    >
      <div className='booking-plan__card-content'>
        <div className='booking-plan__card-row'>
          <div className='booking-plan__card-quantity'>
            <XGSelect
              name='name'
              size='small'
              onChange={handleChange}
              value={`${selectedQuantity}`}
              menuItems={selectOptions}
              disabled={planIsSoldOut(data) || planIsPastDeadline(data)}
              className='booking-plan__card__quantity-select'
              sx={{
                '& .MuiSelect-select': {
                  paddingRight: '32px !important'
                },
                '& .MuiSelect-icon': {
                  right: '4px'
                }
              }}
              startAdornment={
                <InputAdornment
                  position='start'
                  style={{ marginBottom: '5px' }}
                >
                  <User size={20} fill='#475467' />
                </InputAdornment>
              }
            />
            <span className='text-sm-regular'>
              {selectedQuantity === 1
                ? t('bookings:passengerqty1')
                : t('bookings:passengerqty2')}
            </span>
          </div>
          <div className='booking-plan__card-title'>
            <span className='text-md-semibold'>{title}</span>
          </div>
          <div className='booking-plan__card-price'>
            <span className='h3-semibold'>{formatCurrency(planTotal)}</span>
          </div>
        </div>

        <div className='booking-plan__card-row'>
          <div className='booking-plan__card-badges'>
            {planIsPastDeadline(data) && (
              <span className='plan_pastdeadline_badge'>
                <WarningCircle size={16} />
                Booking Deadline Passed
              </span>
            )}
            {planIsSoldOut(data) && (
              <span className='plan_soldout_badge'>
                <XCircle size={16} />
                Sold Out
              </span>
            )}
          </div>
        </div>

        {description && description !== '' && (
          <div className='booking-plan__card-description'>
            <Editor
              content={description}
              editable={false}
              isPreviewing={true}
              id='trip-output-booking-card-description-editor'
              className='third-output-editor--full-width'
            />
          </div>
        )}
      </div>
    </Box>
  );
};

BookingPlanCard.propTypes = {
  data: PropTypes.shape({
    description: PropTypes.string,
    depositamount: PropTypes.number,
    depositduedate: PropTypes.string,
    lastbookdate: PropTypes.string,
    maxavailable: PropTypes.number,
    occupancy: PropTypes.number,
    priceperperson: PropTypes.number,
    tripplanid: PropTypes.number,
    title: PropTypes.string,
    selectedQuantity: PropTypes.number,
    hideTotalBreakdown: PropTypes.bool
  }),
  id: PropTypes.string.isRequired
};
export default BookingPlanCard;
